import {useState, useEffect} from 'react'
/** @jsxImportSource @emotion/react */
import {css} from '@emotion/react'

import useMediaQuery from '@mui/material/useMediaQuery'
import {connect} from 'react-redux'

import {mapKeys, snakeCase} from 'lodash'

import * as Yup from 'yup'
import {withFormik, Form, Field} from 'formik'
import {format} from 'date-fns'

import {
  addHours,
  formatMoney,
  getTopAddress,
  getBottomAddress, 
  combineDateAndTime,
  convertUTCDateToLocalDate
} from '../../../utils'

import {
  show,
  create,
  update,
  showAlert,
  addPerformanceToBand,
  addPerformanceToVenue,
  addPerformanceToArtist
} from '../../../actions'

import {defaultAddress} from '../../../helpers'

import {makeGetPerformanceById, makeGetVenueById} from '../../../selectors'


import Button from '@mui/material/Button'
import Tooltip from '@mui/material/Tooltip'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

import {
  TagField,
  FormTitle,
  AspectBox,
  ImageField,
  FormButton,
  CloseButton,
  FormikTipTap,
  FormikTextField,
  ThreeDotProgress,
  GooglePlacesField,
  AppleMusicLinksField
} from '../../generics'


import EventFormLineup from './EventFormLineup'
import EventFormTickets from './EventFormTickets'
import EventFormDateAndTime from './EventFormDateAndTime'



const styles = {
  root: css`
    background: #fff;
    padding: 40px;
    border-radius: 8px;
    margin: 0 auto;
    
    form {
      width: 100%;
      max-width: 400px;
      display: flex;
      flex-direction: column;
      padding-top: 20px;
      gap: 10px;
      padding-bottom: 50px;
    }
    
    h3 {
      margin-top: 25px;
      margin-bottom: 20px;
      font-weight: 500;
      font-size: 1.2rem;
    }
    
    @media (max-width: 500px) {
      padding: 20px;

      form {
        max-width: 100%;
        padding-top: 10px;
        gap: 8px;
        padding-bottom: 30px;
      }

      h3 {
        font-size: 1rem;
        margin-bottom: 15px;
      }
    }
  `,
  caption: css`
    font-size: 0.9rem;
    color: #636363;
    font-weight: 500;
    margin-bottom: 15px;
  `,
  titleContainer: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 5px;
    margin-top: 0px;
  `,
  nameContainer: css`
    margin-top: 15px;
    margin-bottom: 30px;
  `,
  addressContainer: css`
    padding: 15px 20px;
    margin-top: 10px;
    border-radius: 8px;
    border: 1px solid #90EE90;
    background-color: rgba(193, 225, 193, 0.2);
    
    h4 {
      margin: 0px;
      font-weight: 500;
      font-size: 0.9rem;
      white-space: nowrap;       
      overflow: hidden;          
      text-overflow: ellipsis;   
      display: block; 
    }
    
    p {
      font-size: 0.9rem;
    }
  `,
  rsvpCaption: css`
    font-size: 0.9rem;
    font-weight: 500;
    color: #636363;
  `,
  prefillButtonContainer: css`
    display: flex;
    justify-content: flex-start;
    margin-top: 15px;
  `,
  autofillButton: css`
    min-width: 120px;
    text-transform: uppercase;
    font-size: 0.8rem;
    font-weight: 500;
    
    &.MuiButton-contained {
      box-shadow: none;
    }
  `
}



const EventForm = (props) => {
  const [lastEvent, setLastEvent] = useState(null)

  const isSmallScreen = useMediaQuery(theme => theme.breakpoints.down('md'))

  const {
    close,
    show,
    values,
    errors,
    touched,
    setValues,
    handleBlur,
    setFieldValue,
    setFieldError,
    isSubmitting,
    performance,
    creatorType,
    creatorId
  } = props


  const image = performance?.attributes.image || values.previous_performance_image
  const video = performance?.attributes.video || values.previous_performance_video
  const isAddressEmpty = Object.values(values.address).every((f) => !f)


  // load last event for prefill
  useEffect(() => {
    if (creatorId && creatorType && lastEvent === null) {
      const url = `/${creatorType}s/${creatorId}/previous-performance`
      show(url)
        .then(r => r.data.attributes && setLastEvent(r.data.attributes))
        .catch((e) => {console.log(e)})
    }
  }, [creatorId, creatorType, lastEvent, show])


  const prefill = () => {
    if (!lastEvent) return

    const snakeEvent = mapKeys(lastEvent, (_v, key) => snakeCase(key))
    setValues({
      ...values, 
      ...snakeEvent,
      image: null,
      video: null,
      previous_performance_image: snakeEvent.image,
      previous_performance_video: snakeEvent.video
    })
  }

  const trimValue = (value, field) => (e) => {
    handleBlur(e)
    if (typeof value === 'string' && values[field]) {
      const t = value.trim();
      if (t !== value) setFieldValue(field, t)
    }
  }



  return (
    <div css={styles.root}>
      <div css={styles.titleContainer}>
        <FormTitle title={performance ? 'Edit Event' : 'Create Event'} />

        <CloseButton onClick={close} size='medium' sx={{position: 'static'}} />
      </div>

      {!performance && lastEvent && (
        <div css={styles.prefillButtonContainer}>
          <Tooltip title='Fill the form using your last event'>
            <Button
              css={styles.autofillButton}
              startIcon={<InfoOutlinedIcon />}
              onClick={prefill}
            >
              Pre‑fill
            </Button>
          </Tooltip>
        </div>
      )}

      <Form autoComplete='off'>
        <section>
          <div css={styles.imageContainer}>
            <AspectBox aspect='3:4'>
              <ImageField
                image={image}
                fieldValue={values.image}
                setFieldValue={setFieldValue}
                setFieldError={setFieldError}
              />
            </AspectBox>
          </div>
        </section>

        <section css={styles.nameContainer}>
          <Field
            name='name'
            label='Name'
            variant='filled'
            fullWidth
            autoComplete='off'
            component={FormikTextField}
            onBlur={trimValue(values.name, 'name')}
          />
        </section>


        <section>
          <EventFormDateAndTime
            values={values}
            errors={errors}
            touched={touched}
            setFieldValue={setFieldValue}
          />
        </section>


        <section>
          <h3>Location</h3>
          <Field
            name='location'
            variant='filled'
            fullWidth
            label=''
            placeholder='Address, venue, place...'
            component={GooglePlacesField}
          />

          {!isAddressEmpty &&
            <div css={styles.addressContainer}>
              <h4>{values.location}</h4>
              <p>{getTopAddress(values.address)}</p>
              <p>{getBottomAddress(values.address)}</p>
            </div>
          }
        </section>


        <section>
          <h3>Tickets</h3>
          <EventFormTickets values={values} setFieldValue={setFieldValue} />
        </section>


        <section>
          <h3 style={{marginBottom: '0px'}}>Details</h3>
          <Field
            variant='filled'
            component={FormikTipTap}
            name='description'
            helperText={`${
              (3500 - values.description.length).toLocaleString()
            } characters left`}
          />
        </section>


        <section style={{marginTop: '25px'}}>
          <Field
            name='website'
            label='Website'
            variant='filled'
            fullWidth
            component={FormikTextField}
            placeholder='Link to official event website'
            onBlur={trimValue(values.website, 'website')}
          />
        </section>


        <section>
          <h3 style={{marginBottom: '3px'}}>RSVPs</h3>
          <p css={styles.rsvpCaption}>
            After creating an event, an RSVP button will appear, allowing you 
            to send invitations to your contacts. If you enable public RSVPs, 
            anyone will be able to RSVP to the event—not just those you’ve invited directly.
          </p>

          <FormControlLabel
            sx={{marginTop: '20px'}}
            label='Public RSVP Enabled?'
            control={
              <Field name='rsvp_enabled'>
                {({field}) => (
                  <Checkbox {...field} checked={field.value} />
                )}
              </Field>
            }
          />
        </section>

        <section>
          <h3 style={{marginBottom: '10px'}}>Tags</h3>
          <Field
            name='tags'
            label=''
            variant='filled'
            component={TagField}
            placeholder='Max of 10'
            fullWidth
          />
        </section>


        <section>
          <h3 style={{marginBottom: '5px'}}>Attach Music</h3>
          <p css={styles.caption}>
            Add Apple Music links for the artists and bands performing at this event—we’ll 
            include them so users can easily sample their music.
          </p>
          <Field
            name='apple_music_links'
            label=''
            variant='filled'
            component={AppleMusicLinksField}
            placeholder='Apple Music Artist URL'
            fullWidth
          />
        </section>


        <EventFormLineup
          performers={values.performers}
          creatorType={creatorType}
          isSmallScreen={isSmallScreen}
          setFieldValue={setFieldValue}
        />



        <FormButton type='submit' disabled={isSubmitting}>
          {isSubmitting
            ? <ThreeDotProgress color='#fff' radius={5}/>
            : performance ? 'Update Event' : 'Create Event'
          }
        </FormButton>
      </Form>
    </div>
  )
}



const EnhancedEventForm = withFormik({
  mapPropsToValues: ({performance, venue}) => {
    const attrs      = performance?.attributes || {}
    const venueAttrs = venue?.attributes       || {}

    // Time
    const startUtc = convertUTCDateToLocalDate(attrs.start)
    const endUtc   = attrs.end ? convertUTCDateToLocalDate(attrs.end) : addHours(startUtc, 1)

    return {
      name:                attrs.name  || '',
      image:               attrs.image || null,
      video:               attrs.video || null,
      // Date & time
      date:                startUtc || new Date(),
      start_time:          startUtc || new Date(),
      end_time:            endUtc   || addHours(new Date(), 1),
      repeats:             attrs.repeats || 'never',
      repeat_end:          attrs.repeatEnd ? convertUTCDateToLocalDate(attrs.repeatEnd) : null,
      // Ticketing
      tickets:             attrs.tickets || venueAttrs.website || '',
      price:               attrs.price ? formatMoney(attrs.price).replace('$','') : '',
      // Other
      tags:                attrs.tags || [],
      website:             attrs.website || venueAttrs.website || '',
      performers:          [],
      description:         attrs.description || '',
      rsvp_enabled:        attrs.rsvpEnabled ?? false,
      apple_music_links:   [...new Set(attrs.appleMusicLinks || [])],
      // Location
      address:             attrs.address                || venueAttrs.address                || defaultAddress,
      location:            attrs.location               || venueAttrs.name                   || '',
      coordinates:         attrs.coordinates?.join(' ') || venueAttrs.coordinates?.join(' ') || '',
      location_service:    attrs.locationService        || venueAttrs.locationService        || '',
      location_service_id: attrs.locationServiceId      || venueAttrs.locationServiceId      || '',
    };
  },

  validationSchema: Yup.object().shape({
    name:         Yup.string().required('Required').max(150),
    date:         Yup.date().required('Required'),
    start_time:   Yup.date().required('Required'),
    end_time:     Yup.date().required('Required'),
    location:     Yup.string().required('Required'),
    rsvp_enabled: Yup.boolean()
  }),

  handleSubmit: (values, {props, setSubmitting, setErrors, resetForm}) => {
    const { performance, creatorType, creatorId } = props
    let start = combineDateAndTime(values.date, values.start_time)
    let end   = combineDateAndTime(values.date, values.end_time)
    if (end < start) end = addHours(end, 24)

    const payload = new FormData()

    // Attach image
    if (values.image instanceof File) {
      payload.append('image', values.image)
    }
    // Case where pre-fill is tapped
    if (!values.image && values.previous_performance_image) {
      payload.append('previous_performance_image', values.previous_performance_image)
    }

    // Attach video
    if (values.video instanceof File) {
      payload.append('video', values.video)
    }
    // Case where pre-fill is tapped
    if (!values.video && values.previous_performance_video) {
      payload.append('previous_performance_video', values.previous_performance_video)
    }

    payload.append('name', values.name);
    payload.append('start', format(start, "yyyy-MM-dd'T'HH:mm:ss"))
    payload.append('end', format(end, "yyyy-MM-dd'T'HH:mm:ss"))
    payload.append('tickets', values.tickets)
    payload.append('price', values.price);
    payload.append('location', values.location)
    payload.append('coordinates', values.coordinates)
    payload.append('location_service', values.location_service)
    payload.append('location_service_id', values.location_service_id)
    payload.append('address', JSON.stringify(values.address))
    payload.append('website', values.website)
    payload.append('rsvp_enabled', values.rsvp_enabled)
    payload.append('description', values.description)
    payload.append('tags', JSON.stringify(values.tags))
    payload.append('apple_music_links', JSON.stringify(values.apple_music_links))
    if (values.repeats && values.repeats !== 'never') {
      payload.append('repeats', values.repeats)
      payload.append('repeat_end', format(values.repeat_end, "yyyy-MM-dd'T'HH:mm:ss"))
    }
    if (values.performers.length) {
      payload.append('performers', JSON.stringify(values.performers))
    }

    const baseUrl = `/${creatorType}s/${creatorId}`
    const url = performance
      ? `/performances/${performance.id}`
      : baseUrl + (values.repeats !== 'never' ? '/repeating_performance' : '/performances')

    const action = performance
      ? props.update(url, 'PERFORMANCE', payload, { headers: {'Content-Type':'multipart/form-data'} })
      : props.create(url, 'PERFORMANCE', payload)

    action.then(res => {
      if (!performance) {
        if (creatorType === 'band')   props.addPerformanceToBand(creatorId,   res.data)
        if (creatorType === 'venue')  props.addPerformanceToVenue(creatorId,  res.data)
        if (creatorType === 'artist') props.addPerformanceToArtist(creatorId, res.data)
      }
      props.showAlert(performance ? 'Updated' : 'Created', 'success')
      props.close()
    })
    .catch(err => {
      setSubmitting(false)
      setErrors(err.apiFormErrors(values))
      props.showAlert(err.apiMessage(), 'error')
    })
  }
})(EventForm)




const makeMapStateToProps = () => {
  const getVenue       = makeGetVenueById()
  const getPerformance = makeGetPerformanceById()
  
  return (state, props) => ({
    venue: props.creatorType === 'venue' ? getVenue(state, props.creatorId) : null,
    performance: props.performanceId ? getPerformance(state, props.performanceId) : null
  })
}


export default connect(
  makeMapStateToProps(),
  {show, create, update, showAlert, addPerformanceToBand, addPerformanceToVenue, addPerformanceToArtist}
)(EnhancedEventForm)


